import reportError from 'airship/modules/report-error'
import {stringToSlug} from 'airship/utils/string-to-slug'
import {WebSdkWrapper} from 'airship/utils/web-sdk'

import * as debugLog from '../debug-log'
import * as cache from '../session-cache'
import {AnalyticsBackend} from './backend'
import {
  Action,
  AmplitudeClientInterface,
  AmplitudeClientInterfaceFactory,
  AmplitudeIdentify,
  AmplitudeIdentifyFactory,
  AnalyticsProvider,
  AppcuesEvent,
  AppcuesEventName,
  AppcuesFormFieldSubmitted,
  AppcuesFormSubmitted,
  AppcuesGlobal,
  CompanyGroupProps,
  Context,
  EventBackendProps,
  EventProps,
  SegmentGlobal,
  SimpleAnalyticsProvider,
  UserBackendProps,
  UserNumericPropName,
} from './types'
import {joinEventName} from './util'

export const AMPLITUDE_LABEL = 'Amplitude'

/**
 * GenericClient implements the typical `client.track(name, data)` API. It
 * works as-is with Kissmetrics, Mixpanel, Heap, etc.
 */
export class GenericClient implements AnalyticsProvider {
  public supportsLegacyEvents = true

  public constructor(
    public readonly label: string,
    public readonly client?: SimpleAnalyticsProvider
  ) {}

  public async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps
  ): Promise<boolean> {
    if (!this.client) {
      return false
    }

    const eventName = joinEventName(context, action, description)
    this.client.track(eventName, data)
    return true
  }

  public async trackLegacyEvent(
    eventName: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    if (!this.client) {
      return false
    }

    this.client.track(eventName, data)
    return true
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setUserId(): Promise<boolean> {
    // `track` looks the same across these "generic" platforms, but the
    // identification calls do not. We'll have to start making more
    // platform-specific clients. For now, Amplitude is our chosen platform, so
    // we'll only bother doing it there. We can add more clients later if there
    // are other platforms in the mix.
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setUserProps(): Promise<boolean> {
    // See comment for `setUserId`
    return false
  }
}

/**
 * Segment client to wrap the global library.
 */
export class SegmentClient implements AnalyticsProvider {
  public readonly label: string = 'Segment'
  public readonly supportsLegacyEvents = true

  constructor(public readonly client: SegmentGlobal | undefined) {}

  private async _track(eventName: string, data?: EventProps): Promise<boolean> {
    if (!this.client) {
      return false
    }

    this.client.track(eventName, data)
    return true
  }

  async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps
  ): Promise<boolean> {
    const eventName = joinEventName(context, action, description)
    return this._track(eventName, data)
  }

  async trackLegacyEvent(
    eventName: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    return this._track(eventName, data)
  }

  async setUserProps(props: UserBackendProps): Promise<boolean> {
    if (!this.client) {
      return false
    }

    const id = props.id
    delete props.id
    if (!id) throw new Error('User ID not available')
    this.client.identify(id, props)

    return true
  }
}

export interface AmplitudeClient extends AnalyticsProvider {
  readonly label: typeof AMPLITUDE_LABEL
  readonly backend: AnalyticsBackend
  add(
    name: UserNumericPropName,
    value: number,
    props: EventProps & EventBackendProps
  ): Promise<boolean>
}

/**
 * Amplitude client to wrap the global library.
 */
export class AmplitudeClientImpl implements AmplitudeClient {
  public readonly label = AMPLITUDE_LABEL
  public readonly supportsLegacyEvents = true
  backend: AnalyticsBackend
  client: AmplitudeClientInterface | undefined
  createIdentifyObj: AmplitudeIdentifyFactory

  public constructor(
    backend: AnalyticsBackend,
    clientFactory: AmplitudeClientInterfaceFactory | undefined,
    identifyFactory: AmplitudeIdentifyFactory
  ) {
    this.client = clientFactory && clientFactory()
    this.createIdentifyObj = identifyFactory
    this.backend = backend
  }

  /**
   * Forces a flush of all batched events to be sent to amplitude.
   */
  public async flushEvents(): Promise<boolean> {
    if (!this.client) {
      return Promise.resolve(false)
    }

    try {
      this.client.sendEvents()
    } catch (err) {
      return Promise.resolve(false)
    }
    return Promise.resolve(true)
  }

  public async setGroups(): Promise<void> {
    if (!this.client) {
      return
    }

    // Groups details shouldn't change very often, if at all, but users can jump
    // between apps so we cache details by app key
    const alreadySetCacheKey = `set-amplitude-groups-${this.backend.appKey}`
    if (cache.get(alreadySetCacheKey)) {
      debugLog.log('Not updating user groups for a while')
      return
    }

    const props = await this.backend.getGroupProperties()
    if (!props) {
      return
    }

    const groups: [string, string | number, AmplitudeIdentify][] = []

    if (props.company?.companyId) {
      const companyIdentify = this.getIdentifyObj(props.company)
      if (companyIdentify) {
        groups.push(['Company ID', props.company.companyId, companyIdentify])
      }
    }

    if (props.app?.appKey) {
      const appIdentify = this.getIdentifyObj(props.app)
      if (appIdentify) {
        groups.push(['App Key', props.app.appKey, appIdentify])
      }
    }

    for (const group of groups) {
      const [groupLabel, groupVal, identify] = group
      if (!groupVal) {
        // I don't think this will ever happen
        debugLog.log('Warning: no group value found for %s', groupLabel)
        continue
      }
      debugLog.log('Adding user to group %s: %s', groupLabel, groupVal)
      // Add the user to the group:
      this.client.setGroup(groupLabel, groupVal.toString())
      // Set the group's properties:
      this.client.groupIdentify(groupLabel, groupVal.toString(), identify)
    }

    cache.set(alreadySetCacheKey, true, cache.Duration.Day)
  }

  public async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps
  ): Promise<boolean> {
    const eventName = joinEventName(context, action, description)
    return this._track(eventName, data)
  }

  public async trackLegacyEvent(
    eventName: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    return this._track(eventName, data)
  }

  private async _track(eventName: string, data?: EventProps): Promise<boolean> {
    const client = this.client
    if (!client) {
      return Promise.resolve(false)
    }

    this.setGroups().catch(reportError)

    // Using a verbose Promise here so we can reject an error within the
    // callback.
    return new Promise((resolve, reject) => {
      try {
        client.logEvent(
          eventName,
          data,
          (responseCode: number, responseBody: string) => {
            if (responseCode >= 200 && responseCode < 300) {
              return resolve(true)
            }
            if (responseCode === 0) {
              // This is not an error. There's a "No request sent" message,
              // but it's not actually a problem. See
              // https://github.com/amplitude/Amplitude-Javascript/issues/18#issuecomment-423562300.
              return resolve(true)
            }
            return reject(new Error(`[${responseCode}] ${responseBody}`))
          }
        )
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * https://help.amplitude.com/hc/en-us/articles/115001361248#setting-user-properties
   */
  public async setUserProps(props: UserBackendProps): Promise<boolean> {
    if (!this.client) {
      return false
    }

    if (props.id) {
      this.client.setUserId(props.id)
      delete props.id
    } else {
      // Let's not identify users if we don't have a user ID
      return false
    }

    const identify = this.getIdentifyObj(props)
    if (identify) {
      this.client.identify(identify)
      return true
    }

    return false
  }

  /**
   * Increment a user property by a given value. To decrement, pass in a
   * negative value. If the user property does not have a value set yet, it will
   * be initialized to zero before being incremented.
   *
   * See
   * https://developers.amplitude.com/docs/javascript-sdk-reference#methods-1
   */
  public async add(
    name: UserNumericPropName,
    value: number,
    props: EventProps & EventBackendProps
  ): Promise<boolean> {
    if (!this.client) {
      return false
    }

    let identify = this.getIdentifyObj(props)
    if (identify !== undefined) {
      identify = identify.add(name, value)
      this.client.identify(identify)
      return true
    }

    return false
  }

  /**
   * Build and return an "identify" object. We use these objects to tag an event
   * or group with additional properties.
   */
  public getIdentifyObj(
    props: UserBackendProps | EventBackendProps | CompanyGroupProps
  ): AmplitudeIdentify | undefined {
    if (!this.client) {
      return
    }

    let identify: AmplitudeIdentify | undefined
    for (const [key, val] of Object.entries(props)) {
      if (!identify) {
        identify = this.createIdentifyObj()
      }

      identify = identify?.set(key, val)
    }
    return identify
  }
}

export class AppcuesClient implements AnalyticsProvider {
  public readonly label = 'Appcues'
  public readonly supportsLegacyEvents = true

  private sdk: AppcuesGlobal | undefined

  constructor(sdk?: AppcuesGlobal) {
    this.sdk = sdk
  }

  public async setupFlowTracking(
    analyticsProvider: AnalyticsProvider
  ): Promise<void> {
    this.sdk?.on('all', (name: AppcuesEventName, event: AppcuesEvent) => {
      switch (name) {
        case 'flow_started':
        case 'flow_completed':
        case 'flow_skipped':
          analyticsProvider.track('Appcues', 'Overlay', name, {
            flowId: event.flowId,
            flowName: event.flowName,
          })
          break
        case 'form_submitted':
          const submitEvent = event as AppcuesFormSubmitted
          analyticsProvider.track('Appcues', 'Overlay', name, {
            flowId: submitEvent.flowId,
            flowName: submitEvent.flowName,
            formId: submitEvent.interaction.formId,
            response: JSON.stringify(
              submitEvent.interaction.response.reduce(
                (map: {[key: string]: unknown}, curr) => {
                  map[curr.label] = curr.value
                  return map
                },
                {}
              )
            ),
          })
          break
        case 'form_field_submitted':
          const submitFieldEvent = event as AppcuesFormFieldSubmitted
          analyticsProvider.track('Appcues', 'Overlay', name, {
            flowId: submitFieldEvent.flowId,
            flowName: submitFieldEvent.flowName,
            formId: submitFieldEvent.interaction.formId,
            fieldId: submitFieldEvent.interaction.fieldId,
            fieldLabel: submitFieldEvent.interaction.label,
            fieldValue: submitFieldEvent.interaction.value,
          })
          break
      }
    })
  }

  public async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps
  ): Promise<boolean> {
    const eventName = joinEventName(context, action, description)
    return this._track(eventName, data)
  }

  public async trackLegacyEvent(
    eventName: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    return this._track(eventName, data)
  }

  private async _track(eventName: string, data?: EventProps): Promise<boolean> {
    if (this.sdk) {
      this.sdk.track(eventName, data)
      return Promise.resolve(true)
    }

    return Promise.resolve(false)
  }

  setUserProps(props: UserBackendProps): Promise<boolean> {
    if (this.sdk && props.id) {
      this.sdk.identify(`${props.id}`, props)
      return Promise.resolve(true)
    }
    return Promise.resolve(false)
  }
}

export class AirshipSdkClient implements AnalyticsProvider {
  public label = 'Airship'
  public supportsLegacyEvents = false

  private static BANNED_PROPERTIES: (keyof UserBackendProps)[] = [
    'email',
    'username',
  ]

  private _sdk: Promise<WebSdkWrapper>
  private _context?: Partial<UserBackendProps>

  constructor(sdk: Promise<WebSdkWrapper>) {
    this._sdk = sdk
    // it's possible for the web sdk to not be present, but we don't want it to
    // throw; swallow this error
    sdk.catch((e) => debugLog.log('airship analytics are not available: %s', e))
  }

  async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    try {
      const sdk = await this._sdk
      await sdk._track(`web_${stringToSlug(action)}`, {
        context: stringToSlug(context),
        description: description.toLocaleLowerCase(),
        ...data,
        ...this._context,
      })
      return true
    } catch (e) {
      debugLog.log(
        'failed to submit airship analytics event "%s" with error: %s',
        context,
        e
      )
      return false
    }
  }

  async trackLegacyEvent(): Promise<boolean> {
    throw new Error('not implemented')
  }

  async setUserProps(props: UserBackendProps): Promise<boolean> {
    // the web sdk already identifies itself when included in airship by its own
    // means; this is just for setting sdk event properties that will be used
    // as event context
    this._context = Object.entries(props)
      .filter(
        ([key]) =>
          !AirshipSdkClient.BANNED_PROPERTIES.includes(
            key as keyof UserBackendProps
          )
      )
      .reduce((prev, [key, value]) => {
        prev[key as keyof UserBackendProps] = value
        return prev
      }, {} as Partial<UserBackendProps>)
    return true
  }
}
